import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
// hooks
import useDownloadExternalCertificate from "./useDownloadExternalCertificate"
// api
import { EXTERNAL_CERTIFICATE_PROGRESS_KEYS } from "api"

const RETRY_COUNT = 10
const RETRY_DELAY_MS = 3000

// After a Stripe checkout for the lung cancer screening certificate, the user is
// redirected back to wherever they started the purchase (Finish screen or the
// Settings CME dashboard) with ?certificatePayment=success|cancelled&superCourseId=X.
// On success this fetches (and downloads) the certificate, retrying for a while in
// case the webhook that confirms payment hasn't landed yet (local Stripe CLI
// forwarding in particular can lag several seconds behind the checkout redirect).
const useHandleExternalCertificatePaymentReturn = (): {
  isConfirmingPayment: boolean
  confirmingPaymentSuperCourseId: number
} => {
  const [searchParams, setSearchParams] = useSearchParams()
  const certificatePayment = searchParams.get("certificatePayment")
  const urlSuperCourseId = Number(searchParams.get("superCourseId")) || 0
  const queryClient = useQueryClient()

  const { fetchExternalCertificate } = useDownloadExternalCertificate()

  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false)
  // Captured once into its own state rather than re-derived from searchParams on every
  // render: the params get cleared (synchronously, before the fetch even starts) as
  // part of handling the return, so re-deriving from the URL would drop this to 0 on
  // the very next render - well before the fetch resolves - and silently hide the
  // "confirming payment" UI for the whole (possibly slow) duration of the fetch.
  const [confirmingPaymentSuperCourseId, setConfirmingPaymentSuperCourseId] = useState(0)
  const hasHandledRef = useRef(false)

  useEffect(() => {
    if (!certificatePayment || !urlSuperCourseId || hasHandledRef.current) return

    hasHandledRef.current = true
    const superCourseId = urlSuperCourseId

    setConfirmingPaymentSuperCourseId(superCourseId)

    searchParams.delete("certificatePayment")
    searchParams.delete("superCourseId")
    setSearchParams(searchParams, { replace: true })

    if (certificatePayment !== "success") return

    const attemptDownload = async (attemptsLeft: number) => {
      setIsConfirmingPayment(true)

      const data = await fetchExternalCertificate(superCourseId)

      // Keep any other screen showing this course's payment/OPEN state (Settings CME
      // dashboard, Finish screen) in sync with each attempt, not just on success - so
      // it never gets stuck showing stale data once the webhook eventually lands.
      queryClient.invalidateQueries([EXTERNAL_CERTIFICATE_PROGRESS_KEYS.GET_EXTERNAL_CERTIFICATE_PROGRESSES])

      if (data?.base64Pdf) {
        setIsConfirmingPayment(false)
        return
      }

      if (attemptsLeft > 0) {
        setTimeout(() => attemptDownload(attemptsLeft - 1), RETRY_DELAY_MS)
      } else {
        setIsConfirmingPayment(false)
      }
    }

    attemptDownload(RETRY_COUNT)
  }, [certificatePayment, urlSuperCourseId])

  return { isConfirmingPayment, confirmingPaymentSuperCourseId }
}

export default useHandleExternalCertificatePaymentReturn
